import { AlarmClock, CircleSlash, Clock, type LucideIcon } from 'lucide-react';

import { remainingDaysFromToday, urgencyOf, urgencyText, type Urgency } from '@/lib/urgency';

import { StatusTag, type StatusTone } from './StatusTag';

interface DeadlineBadgeProps {
  /** 마감/만료 시점 — ISO 날짜 또는 오프셋 포함 ISO-8601 */
  endDate: string | null;
  /** 밀도 높은 행(캘린더 시간축) — 아이콘을 한 단계 줄인다 */
  compact?: boolean;
}

/** 등급별 색·글리프 — 의미는 항상 텍스트가 전달하므로 아이콘은 aria-hidden */
const PRESENTATION: Record<Exclude<Urgency, 'normal'>, { tone: StatusTone; icon: LucideIcon }> = {
  critical: { tone: 'destructive', icon: AlarmClock },
  soon: { tone: 'warning', icon: Clock },
  passed: { tone: 'neutral', icon: CircleSlash },
};

/**
 * 마감 임박 배지 — 남은 일수를 텍스트("D-3 마감 임박"·"오늘 마감"·"마감됨")로 노출한다.
 * 색은 보조 수단이고 의미는 단어가 전달한다(WCAG 1.4.1). D-7 초과(normal)는 렌더하지 않는다.
 */
export function DeadlineBadge({ endDate, compact = false }: DeadlineBadgeProps) {
  const days = remainingDaysFromToday(endDate);
  const urgency = urgencyOf(days);
  if (urgency === 'normal') return null;

  const { tone, icon: Icon } = PRESENTATION[urgency];
  return (
    <StatusTag tone={tone}>
      <Icon className={compact ? 'size-3' : undefined} aria-hidden="true" />
      {urgencyText(urgency, days)}
    </StatusTag>
  );
}
